import React, { useEffect, useMemo, useState } from 'react';
import UserService from '@api/Users/UserCtrl';
import { UserType, isUserActive } from '@api/Users/User';
import useOpenModal from '@hooks/useOpenModal';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { AddUserType, ModalSaveUser } from '@components/Admin/Users/Modals/ModalSaveUser';
import { showAlertMessage } from '@utils/Alerts/Message';
import { showIOSAlert } from '@utils/Alerts/Confirm';
import {
    Avatar,
    Box,
    Button,
    Card,
    FormControl,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    Menu,
    IconButton
} from '@mui/material';
import Led from '@components/Utilities/Led';
import { ModalUserInfo } from '@components/Admin/Users/Modals/ModalUserInfo';
import { getProfileName } from '@utils/Profiles';
import { useDispatch } from 'react-redux';
import { setActivityDrawer } from '@store/appStore';
import SearchIcon from '@mui/icons-material/Search';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import { getGridBooleanOperators } from '@mui/x-data-grid';
import SettingsIcon from '@mui/icons-material/Settings';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { canCurrentUser } from '@utils/Permissions';
import { usePermissionSubject, useVisibleBranchOffices } from '@hooks/usePermissionSubject';
import { canManageUser, Capability, getAssignableProfiles } from '@utils/Permissions/Capabilities';

const ListUsersScreen: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    // Las sucursales del filtro: las que ve quien opera (en "Ver como", la del sidebar).
    const branchOffices = useVisibleBranchOffices();
    const userService = useMemo(() => new UserService(), []);
    const [users, setUsers] = useState<UserType[]>([]);
    const [currentUser, setCurrentUser] = useState<UserType | undefined>(undefined);
    const modalAddUser = useOpenModal();
    const modalUserInfo = useOpenModal();
    const [filters, setFilters] = useState({
        search: '',
        profile: 'all',
        branchOffice: 'all'
    });
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
    // El admin general ve y administra a todos; el admin de sucursal, a los de SUS
    // sucursales con un perfil que él puede asignar (plan de Gastos F6). Borrar, los dos
    // (2026-09-30), con la confirmación que explica la consecuencia; nadie a sí mismo.
    // El sujeto es reactivo: previsualizando como Admin de sucursal, cambiar la sucursal
    // en el sidebar cambia a quiénes administra (hasta el 30-sep veía a TODOS: el sujeto
    // conservaba el `['all']` del admin).
    const subject = usePermissionSubject();
    const canDelete = canCurrentUser(Capability.UsersDelete);
    const profileOptions = getAssignableProfiles(subject);
    // Un usuario NO se elimina (decisión del negocio, 2026-09-29): se desactiva y queda
    // su nombre en pedidos, cajas y gastos. Para que la lista no se llene de bajas, los
    // inactivos se ocultan por default; se filtra en cliente (0 lecturas extra).
    const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
    const visibleUsers = useMemo(
        () =>
            users
                .filter((user) => canManageUser(subject, user.profile))
                .filter((user) => statusFilter === 'all' || isUserActive(user) === (statusFilter === 'active')),
        [users, subject, statusFilter]
    );

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: UserType) => {
        setAnchorEl(event.currentTarget);
        setSelectedUser(row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleView = () => {
        if (selectedUser) {
            void onShowUserInfo(selectedUser);
        }
        handleMenuClose();
    };

    const handleEdit = () => {
        if (selectedUser) {
            void onEditUser(selectedUser);
        }
        handleMenuClose();
    };

    const handleToggleStatus = () => {
        if (selectedUser?.uid) {
            void onToggleStatus(selectedUser.uid);
        }
        handleMenuClose();
    };

    /**
     * La bitácora del usuario (M5): es CONTROL DE ACCESO. Sin renglón derivado
     * aunque `users` sí trae `createdAt` en 45/45 — el alta la hace un admin
     * desde esta misma pantalla, así que el primer evento ya queda registrado en
     * cuanto alguien lo active o lo dé de baja. Cuando el alta escriba su propio
     * sello (D9) se puede derivar aquí también.
     */
    const handleActivity = () => {
        const user = selectedUser;
        handleMenuClose();
        if (!user?.uid) return;
        dispatch(setActivityDrawer({ isOpen: true, section: 'user', sectionId: user.uid }));
    };

    const handleDelete = () => {
        if (selectedUser?.uid) {
            showIOSAlert({
                title: 'Eliminar usuario',
                message:
                    `Vas a eliminar a "${selectedUser.profile.name}". Esta acción NO se puede deshacer.\n\n` +
                    'Ya no podrá entrar, y sus pedidos, cajas y gastos se quedarán sin su nombre.\n\n' +
                    'Si solo quieres quitarle el acceso, mejor desactívalo: se conserva su historia y se puede reactivar.',
                buttons: [
                    {
                        text: 'Cancelar',
                        style: 'cancel',
                        onPress: () => {}
                    },
                    {
                        text: 'Eliminar',
                        style: 'destructive',
                        onPress: () => {
                            void onDeleteUser(selectedUser.uid!);
                        }
                    }
                ]
            });
        }
        handleMenuClose();
    };

    const onOpenModalAddUser = () => {
        setCurrentUser(undefined);
        modalAddUser.open();
    };

    const onShowUserInfo = async (user: UserType) => {
        setCurrentUser(user);
        modalUserInfo.open();
    };

    const onEditUser = async (user: UserType) => {
        setCurrentUser(user);
        modalUserInfo.close();
        modalAddUser.open();
    };

    const getData = async () => {
        showLoader();
        try {
            const data = await userService.getUsers(filters);
            setUsers(data);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onAddUser = async (addUser: AddUserType) => {
        showLoader();
        try {
            const saved = await userService.onCreateUser(addUser);
            // Alta con correo interno: quien lo dio de alta tiene que saber con qué entra.
            showAlertMessage(
                !addUser.uid && !addUser.email && saved?.email
                    ? `Usuario creado. Entra con ${saved.email} y su PIN`
                    : 'Usuario guardado exitosamente'
            );
            await getData();
            modalAddUser.toggle();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onToggleStatus = async (userId: string) => {
        showLoader();
        try {
            await userService.toggleStatus(userId);
            await getData();
            showAlertMessage('Estatus de usuario actualizado exitosamente');
            modalUserInfo.close();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onDeleteUser = async (userId: string) => {
        showLoader();
        try {
            await userService.deleteUser(userId);
            await getData();
            showAlertMessage('Usuario eliminado exitosamente');
            modalUserInfo.toggle();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, []);

    useEffect(() => {
        void getData();
    }, [filters.profile, filters.branchOffice]);

    const columns = useMemo<ResponsiveGridColDef<UserType>[]>(
        () => [
            {
                field: 'avatar',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={700}>
                        Avatar
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Avatar alt={row?.profile.name} sx={{ width: 30, height: 30 }} src={row.profile.photo} />
                )
            },
            {
                field: 'name',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={700}>
                        Nombre
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.profile.name}</Typography>
            },
            {
                field: 'phone',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={700}>
                        Teléfono
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.profile.phone}</Typography>
            },
            {
                field: 'email',
                flex: 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={700}>
                        Correo
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.email}</Typography>
            },
            {
                field: 'profile',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={700}>
                        Rol
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{getProfileName(row.profile.profile)}</Typography>
            },
            {
                field: 'status',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={700}>
                        Estado
                    </Typography>
                ),
                renderCell: ({ row }) => <Led value={row.profile.status} />,
                filterOperators: getGridBooleanOperators()
            },
            {
                field: 'actions',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => <SettingsIcon sx={{ color: '#809FB8' }} />,
                renderCell: ({ row }) => (
                    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreVertIcon />
                    </IconButton>
                )
            }
        ],
        []
    );

    return (
        <Box height="100%" p={4}>
            <Stack component={Card} height="100%" spacing={2}>
                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Buscar usuario"
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                    />
                    <FormControl fullWidth>
                        <Select
                            labelId="profile-filter-select-label"
                            id="profile-filter-select"
                            value={filters.profile}
                            onChange={(e) => setFilters({ ...filters, profile: e.target.value })}
                        >
                            <MenuItem value="all">Todos los roles</MenuItem>
                            {profileOptions.map((profile, index) => (
                                <MenuItem key={index} value={profile}>
                                    {getProfileName(profile)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <Select
                            labelId="branch-office-filter-select-label"
                            id="branch-office-filter-select"
                            value={filters.branchOffice}
                            onChange={(e) => setFilters({ ...filters, branchOffice: e.target.value })}
                        >
                            <MenuItem value="all">Todas las sucursales</MenuItem>
                            {branchOffices.map((branchOffice, index) => (
                                <MenuItem key={index} value={branchOffice.id}>
                                    {branchOffice.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <Select
                            id="status-filter-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                        >
                            <MenuItem value="active">Activos</MenuItem>
                            <MenuItem value="inactive">Desactivados</MenuItem>
                            <MenuItem value="all">Activos y desactivados</MenuItem>
                        </Select>
                    </FormControl>
                    <Button fullWidth variant="contained" onClick={onOpenModalAddUser}>
                        Nuevo usuario
                    </Button>
                </Stack>
                <ResponsiveDataGrid
                    searchValue={filters.search}
                    rows={visibleUsers}
                    columns={columns}
                    getRowId={(row) => row.uid}
                    onCellDoubleClick={(cel) => onShowUserInfo(cel.row as UserType)}
                />
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                    <MenuItem onClick={handleView}>Ver</MenuItem>
                    <MenuItem onClick={handleEdit}>Editar</MenuItem>
                    <MenuItem onClick={handleToggleStatus}>
                        {selectedUser?.profile.status ? 'Desactivar' : 'Activar'}
                    </MenuItem>
                    <MenuItem onClick={handleActivity}>Actividad</MenuItem>
                    {canDelete && selectedUser?.uid !== subject.uid && (
                        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                            Eliminar
                        </MenuItem>
                    )}
                </Menu>
                <ModalSaveUser modalAddUser={modalAddUser} user={currentUser} onAddUser={onAddUser} onEmailChanged={getData} />
                {currentUser && (
                    <ModalUserInfo
                        modalUserInfo={modalUserInfo}
                        user={currentUser}
                        onEditUser={onEditUser}
                        onToggleStatus={onToggleStatus}
                        onDeleteUser={canDelete && currentUser.uid !== subject.uid ? handleDelete : undefined}
                    />
                )}
            </Stack>
        </Box>
    );
};

export default ListUsersScreen;
